import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Users, DollarSign, ShoppingBag, Plus, Trash2, Edit, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    brand: 'Jordan',
    description: '',
    price: '',
    original_price: '',
    era: '1990s',
    size: [],
    condition: 'VNDS',
    images: [''],
    category: 'Jordan',
    year: '',
    stock: 1,
    featured: false
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!authLoading && user && !user.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    if (user?.is_admin) {
      fetchData();
    }
  }, [user, isAuthenticated, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`)
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data.products);
      setOrders(ordersRes.data.orders);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price),
        original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
        year: parseInt(productForm.year),
        stock: parseInt(productForm.stock),
        images: productForm.images.filter(img => img.trim())
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Product created');
      }
      
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      brand: product.brand,
      description: product.description,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      era: product.era,
      size: product.size,
      condition: product.condition,
      images: product.images.length ? product.images : [''],
      category: product.category,
      year: product.year.toString(),
      stock: product.stock,
      featured: product.featured
    });
    setProductDialogOpen(true);
  };

  const resetForm = () => {
    setProductForm({
      name: '',
      brand: 'Jordan',
      description: '',
      price: '',
      original_price: '',
      era: '1990s',
      size: [],
      condition: 'VNDS',
      images: [''],
      category: 'Jordan',
      year: '',
      stock: 1,
      featured: false
    });
  };

  const handleSizeToggle = (size) => {
    setProductForm(prev => ({
      ...prev,
      size: prev.size.includes(size)
        ? prev.size.filter(s => s !== size)
        : [...prev.size, size]
    }));
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-concrete-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-chicago-red border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="admin-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-12">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-bebas text-chicago-red text-lg tracking-widest">DASHBOARD</p>
          <h1 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">ADMIN PANEL</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-200 overflow-x-auto">
          {['overview', 'products', 'orders'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-bebas tracking-wider uppercase whitespace-nowrap ${
                activeTab === tab
                  ? 'border-b-2 border-chicago-red text-chicago-red'
                  : 'text-cement-grey hover:text-deep-obsidian'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-chicago-red/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-chicago-red" />
                </div>
                <div>
                  <p className="text-cement-grey text-sm uppercase tracking-wider">Revenue</p>
                  <p className="font-bebas text-3xl">${stats.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-cement-grey text-sm uppercase tracking-wider">Orders</p>
                  <p className="font-bebas text-3xl">{stats.total_orders || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-cement-grey text-sm uppercase tracking-wider">Products</p>
                  <p className="font-bebas text-3xl">{stats.total_products || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-cement-grey text-sm uppercase tracking-wider">Users</p>
                  <p className="font-bebas text-3xl">{stats.total_users || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-bebas text-2xl tracking-wider">INVENTORY</h2>
              <Dialog open={productDialogOpen} onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) {
                  setEditingProduct(null);
                  resetForm();
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-xs font-bold rounded-sm" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-bebas text-2xl tracking-wider">
                      {editingProduct ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleProductSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bebas tracking-wider">NAME</Label>
                        <Input
                          value={productForm.name}
                          onChange={(e) => setProductForm(p => ({...p, name: e.target.value}))}
                          required
                          className="mt-1"
                          data-testid="product-name-input"
                        />
                      </div>
                      <div>
                        <Label className="font-bebas tracking-wider">BRAND</Label>
                        <Select value={productForm.brand} onValueChange={(v) => setProductForm(p => ({...p, brand: v, category: v}))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['Jordan', 'Nike', 'Reebok', 'AND1', 'Converse'].map(b => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="font-bebas tracking-wider">DESCRIPTION</Label>
                      <Textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm(p => ({...p, description: e.target.value}))}
                        required
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="font-bebas tracking-wider">PRICE ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={productForm.price}
                          onChange={(e) => setProductForm(p => ({...p, price: e.target.value}))}
                          required
                          className="mt-1"
                          data-testid="product-price-input"
                        />
                      </div>
                      <div>
                        <Label className="font-bebas tracking-wider">ORIGINAL PRICE ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={productForm.original_price}
                          onChange={(e) => setProductForm(p => ({...p, original_price: e.target.value}))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="font-bebas tracking-wider">YEAR</Label>
                        <Input
                          type="number"
                          value={productForm.year}
                          onChange={(e) => setProductForm(p => ({...p, year: e.target.value}))}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="font-bebas tracking-wider">ERA</Label>
                        <Select value={productForm.era} onValueChange={(v) => setProductForm(p => ({...p, era: v}))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['1980s', '1990s', '2000s'].map(e => (
                              <SelectItem key={e} value={e}>{e}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bebas tracking-wider">CONDITION</Label>
                        <Select value={productForm.condition} onValueChange={(v) => setProductForm(p => ({...p, condition: v}))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['Deadstock', 'VNDS', 'Good'].map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bebas tracking-wider">STOCK</Label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm(p => ({...p, stock: parseInt(e.target.value) || 0}))}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-bebas tracking-wider mb-2 block">SIZES (US)</Label>
                      <div className="flex flex-wrap gap-2">
                        {['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13'].map(size => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => handleSizeToggle(size)}
                            className={`px-3 py-1 border-2 text-sm font-bold ${
                              productForm.size.includes(size)
                                ? 'border-chicago-red bg-chicago-red text-white'
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="font-bebas tracking-wider">IMAGE URL</Label>
                      <Input
                        value={productForm.images[0]}
                        onChange={(e) => setProductForm(p => ({...p, images: [e.target.value]}))}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={productForm.featured}
                        onChange={(e) => setProductForm(p => ({...p, featured: e.target.checked}))}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="featured" className="font-bebas tracking-wider cursor-pointer">FEATURED PRODUCT</Label>
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                      <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-chicago-red hover:bg-chicago-red/90 text-white" data-testid="save-product-btn">
                        {editingProduct ? 'Update' : 'Create'} Product
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left font-bebas tracking-wider">Product</th>
                      <th className="px-6 py-4 text-left font-bebas tracking-wider">Brand</th>
                      <th className="px-6 py-4 text-left font-bebas tracking-wider">Price</th>
                      <th className="px-6 py-4 text-left font-bebas tracking-wider">Stock</th>
                      <th className="px-6 py-4 text-left font-bebas tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.id} data-testid={`admin-product-${product.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.images?.[0] || 'https://via.placeholder.com/40'}
                              alt={product.name}
                              className="w-10 h-10 object-cover"
                            />
                            <div>
                              <p className="font-bold text-sm">{product.name}</p>
                              <p className="text-cement-grey text-xs">{product.era}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">{product.brand}</td>
                        <td className="px-6 py-4 font-bebas">${product.price.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-bold ${
                            product.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {product.stock}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditProduct(product)}
                              className="rounded-sm"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="rounded-sm text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h2 className="font-bebas text-2xl tracking-wider mb-6">ORDERS</h2>
            {orders.length === 0 ? (
              <div className="bg-white border border-gray-200 p-12 text-center">
                <Package className="w-12 h-12 text-cement-grey mx-auto mb-4" />
                <p className="font-bebas text-xl text-cement-grey">NO ORDERS YET</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white border border-gray-200 p-6" data-testid={`admin-order-${order.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">Order ID</p>
                        <p className="font-bold">{order.id.slice(0, 8)}...</p>
                      </div>
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">User</p>
                        <p className="font-bold">{order.user_id?.slice(0, 8)}...</p>
                      </div>
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">Total</p>
                        <p className="font-bebas text-xl">${order.total?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider mb-1">Status</p>
                        <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.id, v)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-cement-grey uppercase tracking-wider mb-2">Items</p>
                      <div className="flex flex-wrap gap-4">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <img
                              src={item.image || 'https://via.placeholder.com/32'}
                              alt={item.name}
                              className="w-8 h-8 object-cover"
                            />
                            <span className="text-sm">{item.name} (x{item.quantity})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
